import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScreenHeading } from './screen-heading'

// Only checks that it is a number: digits with an optional leading "+" and common separators.
// Same pattern as Nexus, which keeps the value as typed.
const PHONE_NUMBER_PATTERN = /^\+?[\d\s\-().]*\d[\d\s\-().]*$/

export function isValidPhoneNumber(phoneNumber: string): boolean {
	return PHONE_NUMBER_PATTERN.test(phoneNumber)
}

type Props = {
	current: string | null
	submitLabel: string
	onSubmit: (value: string) => void
	isSubmitting: boolean
}

export function PhoneNumberScreen({ current, submitLabel, onSubmit, isSubmitting }: Props) {
	const { t } = useTranslation()
	const [phoneNumber, setPhoneNumber] = useState(current ?? '')
	const [showError, setShowError] = useState(false)

	const trimmedPhoneNumber = phoneNumber.trim()
	const isValid = isValidPhoneNumber(trimmedPhoneNumber)

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		if (!isValid) {
			setShowError(true)
			return
		}
		onSubmit(trimmedPhoneNumber)
	}

	return (
		<>
			<ScreenHeading title={t('onboarding.phoneNumber.title')} subtitle={t('onboarding.phoneNumber.subtitle')} />
			<form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
				<Input
					type="tel"
					autoComplete="tel"
					inputMode="tel"
					autoFocus
					placeholder="+358 40 123 4567"
					value={phoneNumber}
					onChange={event => {
						setPhoneNumber(event.target.value)
						setShowError(false)
					}}
					aria-invalid={showError}
					aria-describedby={showError ? 'onboarding-phone-number-error' : undefined}
					disabled={isSubmitting}
					className="h-14 text-base"
				/>
				{showError && (
					<p id="onboarding-phone-number-error" className="text-sm text-destructive">
						{t('onboarding.phoneNumber.invalid')}
					</p>
				)}
				<Button
					type="submit"
					disabled={isSubmitting || trimmedPhoneNumber === ''}
					className="h-14 bg-brand-600 hover:bg-brand-700 text-white"
				>
					{submitLabel}
				</Button>
			</form>
		</>
	)
}
